import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface InventoryFilters {
  item_id?: number;
  item_name?: string;
  serial_number?: string;
  location?: string;
  unit?: string;
  manufacturer?: string;
  show_low_stock?: boolean;
  show_near_expiry?: boolean;
  is_deleted?: boolean;
  positive_only?: boolean;
  limit?: number;
  offset?: number;
}

export interface InventoryApiResponse {
  data: {
    inventories?: any[];
    total?: number;
  };
  status?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class InventoryService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Search/Get all inventories with optional filters
   * Endpoint: GET /api/v1/inventories/
   * Query Parameters: item_id, item_name, serial_number, location, is_deleted, positive_only, limit, offset
   */
  searchInventories(filters?: InventoryFilters): Observable<InventoryApiResponse> {
    let params = new HttpParams();

    if (filters) {
      // Add pagination parameters
      if (filters.limit) {
        params = params.set('limit', filters.limit.toString());
      }
      
      if (filters.offset) {
        params = params.set('offset', filters.offset.toString());
      }

      // Add filter parameters
      if (filters.item_id) {
        params = params.set('item_id', filters.item_id.toString());
      }
      
      if (filters.item_name) {
        params = params.set('item_name', filters.item_name);
      }
      
      if (filters.serial_number) {
        params = params.set('serial_number', filters.serial_number);
      }

      // Default to false if not specified
      if (filters.is_deleted !== undefined) {
        params = params.set('is_deleted', filters.is_deleted.toString());
      } else {
        params = params.set('is_deleted', 'false');
      }

      if (filters.positive_only !== undefined) {
        params = params.set('positive_only', filters.positive_only.toString());
      }
    } else {
      // Default values
      params = params.set('is_deleted', 'false');
    }

    return this.http.get<InventoryApiResponse>(`${this.API_BASE_URL}/api/v1/inventories/`, { 
      params,
      withCredentials: true 
    });
  }

  /**
   * Get single inventory by ID
   * Endpoint: GET /api/v1/inventories/{id}
   */
  getInventoryById(inventoryId: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/inventories/${inventoryId}`, {
      withCredentials: true
    });
  }

  /**
   * Get inventories by item master IDs
   * Endpoint: POST /api/v1/inventories/by-item-master-ids
   */
  getInventoriesByItemMasterIds(itemMasterIds: (string | number)[]): Observable<InventoryApiResponse> {
    return this.http.post<InventoryApiResponse>(
      `${this.API_BASE_URL}/api/v1/inventories/by-item-master-ids`,
      { item_master_ids: itemMasterIds },
      { withCredentials: true }
    );
  }
}

